// React
import { useState } from "react";

// React redux
import { useDispatch, useSelector } from "react-redux";

// @mui material components
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Breadcrumbs,
  CardMedia,
  CircularProgress,
  FormHelperText,
  IconButton,
  Tooltip,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { Close, PhotoCamera } from "@mui/icons-material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";

// Material Dashboard 2 React example components
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// Validator
import { isEmpty } from "validator";

// Service
import { add } from "services/lesson/paragraph/paragraphs.services";

// Constants
import { SERVER_ERROR } from "constants/messages/error";
import { validForm, validField } from "constants/utils/paragraph/validator";
import { errorMessage } from "constants/utils/paragraph/errors.messages";
import { formData } from "constants/utils/paragraph/formdata";

function ModalAdd({ lessonId, finished }) {
  const lessons = useSelector((state) => state.lesson.allLessons);
  const [fields, setFields] = useState({
    rank: { value: "", error: "" },
    title: { value: "", error: "" },
    contents: { value: "", error: "" },
    showed: { value: true, error: "" },
    lesson: { value: `/api/lessons/${lessonId}`, error: "" },
    imageFile: { value: null, error: "" },
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const dispatchApi = useDispatch();
  const modules = {
    toolbar: [
      [{ header: "1" }, { header: "2" }, { font: [] }],
      [{ size: [] }],
      ["bold", "italic", "underline", "strike", "blockquote"],
      [{ list: "ordered" }, { list: "bullet" }, { indent: "-1" }, { indent: "+1" }],
      ["clean"],
    ],
    clipboard: {
      // toggle to add extra line breaks when pasting HTML:
      matchVisual: false,
    },
  };

  const handleChange = (e) => {
    e.preventDefault();
    const { name, value, files, checked } = e.target;
    if (error) setError("");
    setFields((f) => ({
      ...f,
      [name]: {
        value: name === "showed" ? checked : (files && files[0]) || value,
        error: validField(name, value) ? "" : errorMessage[name],
      },
    }));
  };

  const handleBlur = (e) => {
    e.preventDefault();
    const { name, value } = e.target;
    if (isEmpty(value)) setFields((f) => ({ ...f, [name]: { ...f[name], error: "" } }));
  };

  const handleAdd = (e) => {
    let passed = true;
    e.preventDefault();
    // console.log(fields.contents.value);
    if (!validForm(fields)) {
      Object.entries(fields).forEach(([name, { value }]) =>
        setFields((f) => ({
          ...f,
          [name]: { value, error: validField(name, value) ? "" : errorMessage[name] },
        }))
      );
      return;
    }
    if (error) setError("");
    Object.keys(fields).forEach((name) =>
      setFields((f) => ({
        ...f,
        [name]: {
          ...f[name],
          error: "",
        },
      }))
    );
    setLoading(true);
    add(formData(fields))
      .then(({ data }) => {
        const { ...lessonTmp } = lessons.find((lesson) => lesson.id === lessonId);
        lessonTmp.paragraphs = lessonTmp.paragraphs.concat([data]);
        dispatchApi({ type: "UPDATED_LESSON", data: lessonTmp });
        dispatchApi({ type: "ADDED_PARAGRAPH", data });
      })
      .catch((err) => {
        passed = false;
        if (err.request.status) {
          const { violations } = err.response.data;
          violations.forEach((violation) =>
            setFields((f) => ({
              ...f,
              [violation.propertyPath]: { ...f[violation.propertyPath], error: violation.message },
            }))
          );
        } else setError(SERVER_ERROR);
      })
      .finally(() => {
        setLoading(false);
        if (passed) finished();
      });
  };

  return (
    <Dialog open fullWidth maxWidth="xs">
      <DialogTitle>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
          <MDTypography variant="caption" color="text" fontWeight="medium">
            Paragraphe
          </MDTypography>
          <MDTypography variant="caption" color="text" fontWeight="medium">
            Nouveau
          </MDTypography>
        </Breadcrumbs>
      </DialogTitle>
      <MDBox component="form" role="form" onSubmit={handleAdd} noValidate autoComplete="off">
        <DialogContent>
          <MDBox mb={2}>
            <MDInput
              required
              error={Boolean(fields.title.error)}
              type="text"
              name="title"
              label="Titre"
              value={fields.title.value}
              disabled={loading}
              placeholder="Saisir un titre"
              onChange={handleChange}
              onBlur={handleBlur}
              fullWidth
            />
            {fields.title.error && <FormHelperText error>{fields.title.error}</FormHelperText>}
          </MDBox>
          <MDBox mb={2}>
            <MDInput
              required
              error={Boolean(fields.rank.error)}
              type="number"
              name="rank"
              label="Rang"
              value={fields.rank.value}
              disabled={loading}
              placeholder="Saisir un rang"
              onChange={handleChange}
              onBlur={handleBlur}
              fullWidth
            />
            {fields.rank.error && <FormHelperText error>{fields.rank.error}</FormHelperText>}
          </MDBox>
          <MDBox mb={2}>
            <span style={{ fontSize: 13 }}>Contenu</span>
            <ReactQuill
              modules={modules}
              theme="snow"
              value={fields.contents.value}
              onChange={(value) =>
                setFields((f) => ({
                  ...f,
                  contents: {
                    value,
                    error: validField("contents", value) ? "" : errorMessage.contents,
                  },
                }))
              }
            />

            {fields.contents.error && (
              <FormHelperText error>{fields.contents.error}</FormHelperText>
            )}
          </MDBox>
          <MDBox mb={2}>
            <FormControlLabel
              disabled={loading}
              control={
                <Switch
                  name="showed"
                  checked={fields.showed.value}
                  disabled={loading}
                  onChange={handleChange}
                />
              }
              label="Afficher le titre?"
            />
            {fields.showed.error && <FormHelperText error>{fields.showed.error}</FormHelperText>}
          </MDBox>
          <MDBox mb={2}>
            <label htmlFor="icon-button-file">
              <input
                name="imageFile"
                accept="image/*"
                id="icon-button-file"
                type="file"
                style={{ display: "none" }}
                onChange={handleChange}
              />
              <IconButton color="info" aria-label="upload picture" component="span">
                <PhotoCamera />
                <MDTypography variant="button" pl={2} fontWeight="bold">
                  Image
                </MDTypography>
              </IconButton>
            </label>
          </MDBox>
          {Boolean(fields.imageFile.value) && (
            <>
              <Tooltip
                title="Enlever l'image"
                placement="right"
                sx={{ position: "absolute" }}
                onClick={() =>
                  setFields((f) => ({
                    ...f,
                    imageFile: {
                      value: null,
                      error: "",
                    },
                  }))
                }
              >
                <IconButton>
                  <Close />
                </IconButton>
              </Tooltip>
              <CardMedia
                component="img"
                height="140"
                image={URL.createObjectURL(fields.imageFile.value)}
                alt={fields.title.value}
              />
            </>
          )}
          <MDBox mt={2} mb={1}>
            {error && <FormHelperText error>{error}</FormHelperText>}
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={finished} disabled={loading} variant="text" color="info" size="small">
            Retour
          </MDButton>
          <MDButton type="submit" disabled={loading} variant="text" color="info" size="small">
            Ajouter
            {loading && (
              <CircularProgress size="1.5em" color="info" sx={{ position: "absolute" }} />
            )}
          </MDButton>
        </DialogActions>
      </MDBox>
    </Dialog>
  );
}

ModalAdd.propTypes = {
  lessonId: PropTypes.number.isRequired,
  finished: PropTypes.func.isRequired,
};

export default ModalAdd;
